import type {
    InputSignal,
    Type,
    InputSignalWithTransform,
    ModelSignal,
    ValueProvider,
    TemplateRef,
} from '@angular/core';
import type { MatDialogConfig } from '@angular/material/dialog';
import { filter, map, pipe } from 'rxjs';

/** Type alias for all possible input signal types */
/* eslint-disable @typescript-eslint/no-explicit-any -- incorrectly inferred otherwise */
type InputSignalType =
    | InputSignalWithTransform<any, any>
    | InputSignal<any>
    | ModelSignal<any>;

/** Helper type to extract the value type from a signal */
type InputSignalValue<T> = T extends
    | InputSignalWithTransform<infer U, any>
    | InputSignal<infer U>
    | ModelSignal<infer U>
    ? U
    : never;
/* eslint-enable @typescript-eslint/no-explicit-any */

/** Helper type to extract input and model signals from a component */
export type ComponentSignalInputs<TComponent> = {
    [P in keyof TComponent as TComponent[P] extends InputSignalType
        ? P
        : never]: InputSignalValue<TComponent[P]>;
};

/**
 * Helper type to extract properties from a component
 * and infer types from input and model signals
 *
 * @note unfortunately cannot isolate decorator-based inputs
 * and does not work with aliased properties
 */
export type ComponentProperties<TComponent> = {
    [P in keyof TComponent]: TComponent[P] extends InputSignalType
        ? InputSignalValue<TComponent[P]>
        : // eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
          TComponent[P] extends Function
          ? never
          : TComponent[P];
};

export interface IDialogPosition {
    top: number;
    right: number;
    bottom: number;
    left: number;
}

export interface QpxOpenDialogOptions<TComponent> {
    component: Type<TComponent>;
    componentInputs?: Partial<ComponentProperties<TComponent>>;
}

export type DialogSize = 'sm' | 'md' | 'lg' | 'xl';
export type ConfirmButtonTheme =
    | 'primary'
    | 'accent'
    | 'info'
    | 'success'
    | 'caution'
    | 'destructive';
export type DialogPresentation = 'standard' | 'confirmation';
export type ProcessingAppearance = 'body' | 'button' | 'both';

export interface DialogConfirmButtonOptions {
    label: string;
    icon: string;
    theme: ConfirmButtonTheme;
}

export interface DialogCancelButtonOptions {
    label: string;
    icon?: string;
}

export type DialogConfirmButtonInput =
    | false
    | string
    | Partial<DialogConfirmButtonOptions>;
export type DialogCancelButtonInput =
    | false
    | string
    | Partial<DialogCancelButtonOptions>;

export type QpxDialogConfig = Omit<MatDialogConfig<unknown>, 'data'> & {
    providers?: ValueProvider[];
    size?: DialogSize;
    fullscreenBelow?: 'tablet' | 'tablet768';
};

export type DialogResult =
    | boolean
    | { confirm: boolean; data: unknown }
    | undefined;

export function isBooleanDialogResult(result: DialogResult): result is boolean {
    return typeof result === 'boolean';
}

export function isObjectDialogResult(
    result: DialogResult
): result is { confirm: boolean; data: unknown } {
    return (
        typeof result === 'object' &&
        result != null &&
        'confirm' in result &&
        typeof result.confirm === 'boolean' &&
        'data' in result
    );
}

/** Type guard function type for dialog result data */
export type DialogResultTypeGuard<T> = (data: unknown) => data is T;

/**
 * Maps a dialog result to a standardized record with optional type narrowing for the data
 * @param typeGuardFn Optional type guard function to narrow the data type
 */
export function toDialogResultTypedRecord<T = unknown>(
    typeGuardFn?: DialogResultTypeGuard<T>
) {
    return map((result: DialogResult) => {
        if (isBooleanDialogResult(result)) {
            return { confirm: result, data: undefined as T | undefined };
        } else if (isObjectDialogResult(result)) {
            const typedResult =
                typeGuardFn &&
                result.data !== undefined &&
                typeGuardFn(result.data)
                    ? { ...result, data: result.data as T }
                    : {
                          ...result,
                          data: result.data as T | undefined,
                      };
            return typedResult;
        }
        return { confirm: false, data: undefined as T | undefined };
    });
}

/** Maps a dialog result to a simple boolean confirmation status */
export function toDialogConfirmation() {
    return map((result: DialogResult): boolean => {
        if (isBooleanDialogResult(result)) {
            return result;
        } else if (isObjectDialogResult(result)) {
            return result.confirm;
        }
        return false;
    });
}

/** Maps a dialog result to yes/no confirmation and filters for confirm only */
export function isDialogConfirmedOnly() {
    return pipe(
        toDialogConfirmation(),
        filter((confirmed) => !!confirmed)
    );
}

export type ConfirmationStatus =
    | 'default'
    | 'info'
    | 'success'
    | 'caution'
    | 'destructive';

export type ConfirmationAction = string | { label: string; icon?: string };

export interface ConfirmationOptions {
    title?: string;
    status?: ConfirmationStatus;
    confirmButton?: ConfirmationAction;
    cancelButton?: false | ConfirmationAction;
    text1?: string;
    text2?: string;
    text3?: string;
    /** Content follows Angular's HTML sanitization rules. */
    contentHtml?: string;
    contentTpl?: TemplateRef<unknown>;
    contentTplContext?: Record<string, unknown>;
}

/** Confirmation layout and initial focus are owned by the launcher. */
export type ConfirmationConfig = Omit<
    Partial<QpxDialogConfig>,
    | 'size'
    | 'panelClass'
    | 'fullscreenBelow'
    | 'width'
    | 'minWidth'
    | 'maxWidth'
    | 'height'
    | 'minHeight'
    | 'maxHeight'
    | 'position'
    | 'hasBackdrop'
    | 'autoFocus'
>;
